import { useParams, Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    requestAnimationFrame(() => {
      el.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 pt-20">
        <i className="ri-file-damage-line text-5xl text-foreground-300 mb-4" />
        <h1 className="text-xl font-bold text-foreground-800 mb-2">文章未找到</h1>
        <p className="text-sm text-foreground-500 mb-6">您访问的文章可能已被删除或不存在</p>
        <Link to="/" className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors">
          返回首页 <i className="ri-arrow-right-line" />
        </Link>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  if (relatedArticles.length < 3) {
    const more = articles
      .filter((a) => a.id !== article.id && !relatedArticles.find((r) => r.id === a.id))
      .slice(0, 3 - relatedArticles.length);
    relatedArticles.push(...more);
  }

  const category = categories.find((c) => c.slug === article.category);
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    description: article.summary,
    image: { '@type': 'ImageObject', url: article.image },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: { '@type': 'Person', name: article.author },
    publisher: {
      '@type': 'Organization',
      name: '51CG吃瓜特色',
      logo: { '@type': 'ImageObject', url: `${typeof window !== 'undefined' ? window.location.origin : ''}/logo.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': typeof window !== 'undefined' ? window.location.href : '' },
    articleSection: article.categoryName,
    keywords: article.tags.join(', '),
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `${typeof window !== 'undefined' ? window.location.origin : ''}/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: typeof window !== 'undefined' ? window.location.href : '' },
    ],
  };

  const faqLd = article.faqs
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: article.faqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }
    : null;

  const jsonLdArray = [articleLd, breadcrumbLd];
  if (faqLd) jsonLdArray.push(faqLd);

  return (
    <>
      <SEOHead
        title={article.metaTitle || article.title}
        description={article.metaDescription || article.summary}
        keywords={article.tags.join(', ')}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.publishDate}
        articleAuthor={article.author}
        articleSection={article.categoryName}
        articleTags={article.tags}
        jsonLd={jsonLdArray}
      />

      {/* Article Hero */}
      <section className="relative min-h-[300px] md:min-h-[400px] flex items-end overflow-hidden pt-20 md:pt-24 pb-8 md:pb-12">
        <div className="absolute inset-0">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/20" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <Breadcrumb items={breadcrumbItems} />
          <div className="mt-4 flex items-center gap-3 mb-3">
            <Link
              to={`/category/${article.category}`}
              className="px-2.5 py-1 rounded-md text-xs font-bold bg-accent-500 text-foreground-950 hover:bg-accent-400 transition-colors"
            >
              {article.categoryName}
            </Link>
            <span className="text-xs text-white/60">{article.publishDate}</span>
            <span className="text-xs text-white/60">{article.readTime}阅读</span>
          </div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight max-w-4xl">
            {article.title}
          </h1>
          <div className="mt-4 flex items-center gap-3 text-sm text-white/70">
            <span className="flex items-center gap-1.5">
              <span className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center text-xs font-bold text-foreground-950">
                {article.author[0]}
              </span>
              {article.author}
            </span>
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" /> {(article.views / 10000).toFixed(1)}万阅读
            </span>
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-400" /> 热度 {article.hotIndex}
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12 bg-background-50">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div ref={contentRef} className="flex-1 min-w-0 max-w-3xl">
            {/* Summary Card */}
            <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-double-quotes-l text-sm" />
                </div>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed italic">
                  {article.summary}
                </p>
              </div>
            </div>

            {/* Content */}
            <article
              className="prose prose-sm md:prose-base max-w-none prose-headings:text-foreground-950 prose-headings:font-bold prose-p:text-foreground-700 prose-p:leading-relaxed prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground-900 prose-img:rounded-lg"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to="/category/hot-gossip"
                    className="px-3 py-1 rounded-full text-xs font-medium bg-background-200 text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors whitespace-nowrap"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ Section */}
            {article.faqs && article.faqs.length > 0 && (
              <div className="mt-10 pt-8 border-t border-background-200/70">
                <h3 className="text-lg font-bold text-foreground-950 mb-5 flex items-center gap-2">
                  <i className="ri-question-answer-line text-accent-500" />
                  常见问题
                </h3>
                <div className="space-y-4">
                  {article.faqs.map((faq, idx) => (
                    <div key={idx} className="bg-background-100 rounded-lg p-4 md:p-5 border border-background-200/70">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-start gap-2">
                        <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">Q</span>
                        {faq.question}
                      </h4>
                      <p className="text-sm text-foreground-600 leading-relaxed pl-7">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Author Box */}
            <div className="mt-10 p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/70 flex items-start gap-4">
              <div className="w-12 h-12 flex-shrink-0 rounded-full bg-accent-500 flex items-center justify-center text-lg font-bold text-foreground-950">
                {article.author[0]}
              </div>
              <div>
                <p className="text-sm font-bold text-foreground-950">{article.author}</p>
                <p className="text-xs text-foreground-500 mt-1">51CG吃瓜特色 · 资深娱记</p>
                <p className="text-xs text-foreground-600 mt-2 leading-relaxed">
                  专注于娱乐圈热点追踪与深度分析，致力于为广大吃瓜群众提供第一手资讯与专业解读。
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="w-full lg:w-80 flex-shrink-0 space-y-8">
            {/* Category Info */}
            {category && (
              <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 mb-3">所属分类</h3>
                <Link
                  to={`/category/${category.slug}`}
                  className="flex items-center gap-3 p-3 rounded-md bg-background-50 hover:bg-accent-50 border border-background-200/50 hover:border-accent-200 transition-all group"
                >
                  <img src={category.image} alt={category.name} className="w-12 h-12 rounded-md object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">{category.name}</p>
                    <p className="text-xs text-foreground-500">{category.articleCount} 篇文章</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </Link>
              </div>
            )}

            {/* Related Articles */}
            <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
              <h3 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-links-line text-accent-500" />
                相关推荐
              </h3>
              <div className="space-y-3">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="compact" />
                ))}
              </div>
            </div>

            {/* Subscribe */}
            <div className="rounded-lg overflow-hidden border border-background-200/70">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20lime%20green%20glowing%20notification%20bell%20on%20dark%20background%2C%20subscribe%20concept%20art%2C%20clean%20modern%20digital%20illustration&width=400&height=200&seq=article-sidebar-promo&orientation=landscape"
                alt="订阅"
                className="w-full h-28 object-cover"
              />
              <div className="p-4 bg-background-100">
                <h4 className="text-sm font-bold text-foreground-950 mb-1">喜欢这篇文章？</h4>
                <p className="text-xs text-foreground-500 mb-3">订阅51CG吃瓜，不错过任何精彩内容</p>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  立即订阅 <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Related Section */}
      {relatedArticles.length > 0 && (
        <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14 bg-background-100">
          <div className="flex items-end justify-between mb-6 md:mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950">猜您还喜欢</h2>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
              查看更多 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {relatedArticles.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}